import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stack,
  Typography,
  Box,
  Card,
  CardContent,
  Switch,
  Tooltip,
  Alert,
  CircularProgress,
  Fab,
} from '@mui/material';
import { addDaysAsMicros, getStartOfDayAsMicros } from '@/utilities/timeUtils';
import {
  ETransportFreightData,
  ETransportFreightPlutoDto,
  ETransportFreightPlutoDtoSource,
  ETransportTargetFreightPlutoDto,
  MinimalTarget,
} from '@/generated/model';
import { addFreight } from '@/generated/client/etransport/etransport';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { fullDateNoYearNoSeconds } from '@/utilities/dateUtils';
import { getPlateNumber, mapAvaliableTargetIDs } from './utils/utils';
import { startOfDay } from 'date-fns';
import { DatePickerSelect } from '@/components/common/date/DatePickerSelect';
import { useNotificationStore } from '@/stores/notificationstore';
import { ConfirmLeaveDialog } from './ConfirmLeaveDialog';

export function FreightSubmitForm({
  targets,
  onRefetch,
  setCurrentFreight,
  setIsClosingSubmitForm,
  setOpenFreightSubmitForm,
}: {
  targets: MinimalTarget[] | undefined;
  onRefetch: () => void;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;
  setIsClosingSubmitForm: Dispatch<SetStateAction<boolean>>;
  setOpenFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t, i18n } = useTranslation();
  const [minDate] = useState<Date>(() => new Date(Date.now() - 15 * 24 * 60 * 60 * 1000)); //15 days ago
  const [currentValue, setCurrentValue] = useState<Date | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [openConfirmLeaveDialog, setOpenConfirmLeaveDialog] = useState<boolean>(false);
  const [isInRomania, setIsInRomania] = useState<boolean>(false);
  const [startTimeMicros] = useState<number>(() => Date.now() * 1000);
  const [freightFormData, setFreightFormData] = useState<ETransportFreightPlutoDto>({
    validityStartTimeMicros: undefined,
    validityEndTimeMicros: undefined,
    uit: undefined,
    vat: undefined,
    isDeleted: false,
    isSynchronized: false,
    source: ETransportFreightPlutoDtoSource.MANUAL,
    firstReceivedTimeMicros: startTimeMicros,
    lastUpdatedTimeMicros: startTimeMicros,
    lastSentTimeMicros: undefined,
    targetFreights: [],
  });
  const [targetFreightFormData, setTargetFreightFormData] = useState<ETransportTargetFreightPlutoDto>({
    creationTimeMicros: undefined,
    startTimeMicros: undefined,
    endTimeMicros: undefined,
    targetId: undefined,
    uit: undefined,
    trailer1: undefined,
    trailer2: undefined,
  });
  const [freightDataFormData, setFreightDataFormData] = useState<ETransportFreightData>({
    declarantName: undefined,
    fromAddress: undefined, //dont use it yet
    toAddress: undefined, //dont use it yet
    document: undefined, //dont use it yet
  });
  const isFormIncomplete: boolean =
    !freightFormData.uit ||
    !freightFormData.vat ||
    !freightFormData.validityStartTimeMicros ||
    !targetFreightFormData.targetId;

  function handleSubmit(): void {
    completeFreight();
    setOpenConfirmDialog(true);
  }

  function handleCloseAnimation(): void {
    setIsClosingSubmitForm(true);
    setTimeout(() => {
      setIsClosingSubmitForm(false);
    }, 400);
  }

  function handleClose(): void {
    setOpenFreightSubmitForm(false);
    handleCloseAnimation();
  }

  function handleDatePicker(date: Date) {
    if (!date) return;
    const validityStartTimeDate = startOfDay(date);
    const validityStartTimeMicros = validityStartTimeDate.getTime() * 1000;
    setFreightFormData({
      ...freightFormData,
      validityStartTimeMicros: validityStartTimeMicros,
    });
    setTargetFreightFormData({
      ...targetFreightFormData,
      startTimeMicros: validityStartTimeMicros,
    });
    setCurrentValue(validityStartTimeDate);
  }

  function handleLeave(): void {
    if (!isFormIncomplete) {
      setOpenConfirmLeaveDialog(true);
    } else {
      handleClose();
    }
  }

  function calculateValidityEndMicros(micros: number): number {
    return isInRomania ? addDaysAsMicros(micros, 5) : addDaysAsMicros(micros, 15);
  }

  function completeFreight(): void {
    const validityEndTimeMicros = getStartOfDayAsMicros(
      calculateValidityEndMicros(freightFormData.validityStartTimeMicros || 0)
    );
    const updatedTargetFreight = { ...targetFreightFormData, endTimeMicros: validityEndTimeMicros };
    setTargetFreightFormData(updatedTargetFreight);
    setFreightFormData({
      ...freightFormData,
      validityEndTimeMicros: validityEndTimeMicros,
      freightData: freightDataFormData,
      targetFreights: [updatedTargetFreight],
    });
  }

  const avaliableTargetIDs = useMemo(() => {
    return mapAvaliableTargetIDs(targets);
  }, [targets]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        backgroundColor: 'white',
      }}
    >
      <div className="filterBar">
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: 1 }}>
          <ArrowBackIcon
            sx={{ fontSize: '1.5rem', color: 'text.secondary', cursor: 'pointer' }}
            onClick={handleLeave}
          />
          <Typography variant="h5" sx={{ color: 'black' }}>
            {t('etransport.freightfrom.addnew')}
          </Typography>
        </Box>
      </div>
      {isFormIncomplete && <Alert severity="warning">{t('etransport.freightfrom.warning')}</Alert>}
      <div className="freightOverlay">
        <Card elevation={3} sx={{ padding: '1px', height: '100%' }}>
          <CardContent sx={{ overflowY: 'auto' }}>
            <Stack spacing={3}>
              <TextField
                label={`${t('etransport.freightfrom.uit')} (UIT)`}
                value={freightFormData.uit}
                onChange={(e) => {
                  setFreightFormData({ ...freightFormData, uit: e.target.value });
                  setTargetFreightFormData({ ...targetFreightFormData, uit: e.target.value });
                }}
                placeholder="UIT"
                required
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />
              <TextField
                label={`${t('etransport.freightfrom.vat')} (VAT)`}
                value={freightFormData.vat}
                onChange={(e) => {
                  setFreightFormData({ ...freightFormData, vat: e.target.value });
                }}
                placeholder="VAT"
                required
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />
              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                <DatePickerSelect
                  sx={{ width: '100%', backgroundColor: 'var(--mui-palette-contrast-background)' }}
                  label={t('etransport.freightfrom.validitystart') + ' *'}
                  currentValue={currentValue}
                  minDate={minDate}
                  setSelectedDate={handleDatePicker}
                />
                <Box display="flex" alignItems="center">
                  <Typography variant="caption" color="text.secondary" fontSize="1rem">
                    {t('etransport.freightfrom.isromania')}
                  </Typography>
                  <Switch
                    required
                    size="medium"
                    checked={isInRomania}
                    onChange={(e) => {
                      const state: boolean = e.target.checked;
                      setIsInRomania(state);
                    }}
                    sx={{
                      transform: 'scale(1.2)',
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'var(--mui-palette-secondary-main)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'var(--mui-palette-secondary-dark)',
                      },
                    }}
                  />
                  <Tooltip title={t('etransport.freightfrom.isromaniainfo')} arrow>
                    <InfoIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                {freightFormData.validityStartTimeMicros && (
                  <Box display="flex" alignItems="center" width="100%">
                    <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                      {`${t('etransport.freightfrom.validityend')}: ${fullDateNoYearNoSeconds(calculateValidityEndMicros(freightFormData.validityStartTimeMicros) || 0, i18n.language, t)}`}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Autocomplete
                options={avaliableTargetIDs}
                value={targetFreightFormData.targetId}
                getOptionLabel={(option) => {
                  return getPlateNumber(targets, option) || 'Unkown';
                }}
                onChange={(_, newValue) => {
                  setTargetFreightFormData({ ...targetFreightFormData, targetId: newValue || 0 });
                }}
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('etransport.freightfrom.plate')}
                    placeholder={t('etransport.freightfrom.plateselect')}
                    required
                  />
                )}
              />

              <TextField
                label={t('etransport.freightfrom.trailer1')}
                value={targetFreightFormData.trailer1}
                onChange={(e) => {
                  setTargetFreightFormData({ ...targetFreightFormData, trailer1: e.target.value });
                }}
                placeholder={t('etransport.freightfrom.platewrite')}
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />

              <TextField
                label={t('etransport.freightfrom.trailer2')}
                value={targetFreightFormData.trailer2}
                onChange={(e) => {
                  setTargetFreightFormData({ ...targetFreightFormData, trailer2: e.target.value });
                }}
                placeholder={t('etransport.freightfrom.platewrite')}
                fullWidth
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />

              <TextField
                label={t('etransport.freightfrom.partner')}
                value={freightDataFormData.declarantName}
                onChange={(e) => {
                  setFreightDataFormData({ ...freightDataFormData, declarantName: e.target.value });
                }}
                placeholder="..."
                fullWidth
                multiline
                rows={3}
                sx={{ backgroundColor: 'var(--mui-palette-contrast-background)' }}
              />
            </Stack>
            <Stack
              spacing={1}
              sx={{
                position: 'absolute',
                bottom: 20,
                right: 18,
              }}
            >
              <>
                <Fab
                  variant="extended"
                  onClick={handleSubmit}
                  aria-label={t('etransport.freightfrom.save')}
                  disabled={isFormIncomplete}
                  sx={{
                    boxShadow: 6,
                    backgroundColor: 'var(--mui-palette-secondary-main)',
                    color: 'white',
                    border: '2px solid white',
                    '&:hover': {
                      backgroundColor: 'var(--mui-palette-secondary-light)',
                    },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ mr: 1 }} /> {t('etransport.freightfrom.save')}
                </Fab>
                <Fab
                  variant="extended"
                  onClick={handleLeave}
                  aria-label={t('etransport.freightfrom.cancel')}
                  sx={{
                    boxShadow: 6,
                    backgroundColor: 'white',
                    color: 'var(--mui-palette-secondary-main)',
                    border: '2px solid var(--mui-palette-secondary-main)',
                    '&:hover': {
                      backgroundColor: 'rgba(146, 102, 21, 0.1)',
                    },
                  }}
                >
                  <HighlightOffOutlinedIcon sx={{ mr: 1 }} />
                  {t('etransport.freightfrom.cancel')}
                </Fab>
              </>
            </Stack>
          </CardContent>
        </Card>
      </div>

      <ConfirmFreightDialog
        open={openConfirmDialog}
        targets={targets}
        freight={freightFormData}
        onRefetch={onRefetch}
        setCurrentFreight={setCurrentFreight}
        setOpenConfirmDialog={setOpenConfirmDialog}
        setOpenFreightSubmitForm={setOpenFreightSubmitForm}
      />

      <ConfirmLeaveDialog
        uit={freightFormData.uit}
        open={openConfirmLeaveDialog}
        setOpenConfirmDialog={setOpenConfirmLeaveDialog}
        handleCloseDialog={handleClose}
      />
    </Box>
  );
}

function ConfirmFreightDialog({
  open,
  targets,
  freight,
  onRefetch,
  setCurrentFreight,
  setOpenConfirmDialog,
  setOpenFreightSubmitForm,
}: {
  open: boolean;
  targets: MinimalTarget[] | undefined;
  freight: ETransportFreightPlutoDto;
  onRefetch: () => void;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;

  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;
  setOpenFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t, i18n } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const targetFreightData: ETransportTargetFreightPlutoDto | undefined = freight.targetFreights?.[0];

  function handleClose(): void {
    setError(null);
    setOpenConfirmDialog(false);
  }

  async function handleSave(): Promise<void> {
    setIsSaving(true);
    try {
      await addFreight(freight);
      setCurrentFreight(freight);
      showNotification('success', t('etransport.targetform.success'), undefined, 10);
      onRefetch();
      handleClose();
      setOpenFreightSubmitForm(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('etransport.freightfrom.apierror'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        <Typography variant="h6"> {t('etransport.freightfrom.dialog.title')}</Typography>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <Typography variant="body1" gutterBottom>
          {t('etransport.freightfrom.dialog.confirmtext')}
        </Typography>
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'var(--mui-palette-contrast-background)', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>{`${t('etransport.freightfrom.uit')} (UIT)`}:</strong> {freight.uit}
          </Typography>
          <Typography variant="body2">
            <strong>{`${t('etransport.freightfrom.vat')} (VAT)`}:</strong> {freight.vat}
          </Typography>
          {freight.freightData?.declarantName && (
            <Typography variant="body2">
              <strong>{t('etransport.freightfrom.partner')}:</strong> {freight.freightData.declarantName}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>{t('etransport.freightfrom.validitystart')}: </strong>{' '}
            {fullDateNoYearNoSeconds(freight.validityStartTimeMicros || 0, i18n.language, t)}
          </Typography>
          <Typography variant="body2">
            <strong>{t('etransport.freightfrom.validityend')}:</strong>{' '}
            {fullDateNoYearNoSeconds(freight.validityEndTimeMicros || 0, i18n.language, t)}
          </Typography>
          <Typography variant="body2">
            <strong>{t('etransport.freightfrom.plate')}:</strong>{' '}
            {getPlateNumber(targets, targetFreightData?.targetId || 0) || 'Unkown'}
          </Typography>
          {targetFreightData?.trailer1 && (
            <Typography variant="body2">
              <strong>{t('etransport.freightfrom.trailer1')}:</strong> {targetFreightData.trailer1}
            </Typography>
          )}
          {targetFreightData?.trailer2 && (
            <Typography variant="body2">
              <strong>{t('etransport.freightfrom.trailer2')}:</strong> {targetFreightData.trailer2}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            backgroundColor: 'white',
            color: 'var(--mui-palette-secondary-main)',
            border: '2px solid var(--mui-palette-secondary-main)',
            '&:hover': {
              backgroundColor: 'rgba(146, 102, 21, 0.1)',
            },
          }}
        >
          {t('etransport.freightfrom.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving}
          sx={{
            backgroundColor: 'var(--mui-palette-secondary-main)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--mui-palette-secondary-light)',
            },
          }}
        >
          {isSaving && <CircularProgress size={20} />}
          {t('etransport.freightfrom.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
